import {
  Button,
  FormField,
  Icon,
  Input,
  List,
  ListRow,
  MapView,
  SegmentedControl,
  Text,
  Textarea,
  useToast,
  type GeoPosition,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FormDialog } from '../../../app/FormDialog';
import { errorCode, errorMessage, formErrors } from '../../../lib/errors';
import { useMeta } from '../../system/useMeta';
import {
  useGeocode,
  useSaveLocation,
  type GeocodeResult,
  type Location,
  type LocationKind,
} from '../api';
import styles from './forms.module.css';

export interface LocationFormDrawerProps {
  open: boolean;
  onClose: () => void;
  location?: Location;
  /** After creating; defaults to opening the new location. */
  onCreated?: (location: Location) => void;
}

const round = (v: number) => Math.round(v * 1e6) / 1e6;

/** Create or edit a location with map, address search and GPS (LOC-001, LOC-002). */
export function LocationFormDrawer({
  open,
  onClose,
  location,
  onCreated,
}: LocationFormDrawerProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const meta = useMeta();
  const save = useSaveLocation();
  const geocode = useGeocode();
  const [name, setName] = useState(location?.name ?? '');
  const [kind, setKind] = useState<LocationKind>(location?.kind ?? 'home');
  const [position, setPosition] = useState<GeoPosition | null>(
    location ? { latitude: location.latitude, longitude: location.longitude } : null,
  );
  const [address, setAddress] = useState(location?.address ?? '');
  const [access, setAccess] = useState(location?.access ?? '');
  const [ownerContact, setOwnerContact] = useState(location?.owner_contact ?? '');
  const [notes, setNotes] = useState(location?.notes ?? '');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[] | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState(false);
  const [missingPosition, setMissingPosition] = useState(false);
  const errors = formErrors(save.error, {
    name: ['location_name.', 'location.name_taken'],
    position: ['geo.'],
  });

  const pick = (p: GeoPosition) => {
    setPosition({ latitude: round(p.latitude), longitude: round(p.longitude) });
    setMissingPosition(false);
  };
  const close = () => {
    save.reset();
    geocode.reset();
    onClose();
  };
  const search = () => {
    if (query.trim().length < 3) return;
    geocode.mutate(query.trim(), { onSuccess: setResults });
  };
  const locate = () => {
    if (!navigator.geolocation) {
      setLocateError(true);
      return;
    }
    setLocating(true);
    setLocateError(false);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLocating(false);
        pick({ latitude: p.coords.latitude, longitude: p.coords.longitude });
      },
      () => {
        setLocating(false);
        setLocateError(true);
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };
  const submit = () => {
    if (!position) {
      setMissingPosition(true);
      return;
    }
    const body = {
      name,
      kind,
      latitude: position.latitude,
      longitude: position.longitude,
      address: address || null,
      access: access || null,
      owner_contact: ownerContact || null,
      notes: notes || null,
    };
    save.mutate(
      { id: location?.id, body, version: location?.version },
      {
        onSuccess: (saved) => {
          toast.show(location ? t('apiary.locationForm.saved') : t('apiary.locationForm.created'));
          close();
          if (!location) {
            if (onCreated) onCreated(saved);
            else navigate(`/standorte/${saved.id}`);
          }
        },
      },
    );
  };

  const geocodeUnavailable =
    meta.data?.geocoding === false || errorCode(geocode.error) === 'geocoding.unavailable';

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      size="lg"
      title={location ? t('apiary.locationForm.editTitle') : t('apiary.locationForm.createTitle')}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={save.isPending}
      error={errors.general}
    >
      <FormField label={t('apiary.locationForm.name')} required error={errors.field.name}>
        {(p) => (
          <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        )}
      </FormField>
      <SegmentedControl
        label={t('apiary.locationForm.kind')}
        value={kind}
        onChange={setKind}
        options={[
          { value: 'home', label: t('apiary.kind.home') },
          { value: 'migratory', label: t('apiary.kind.migratory') },
        ]}
      />
      {!geocodeUnavailable && (
        <div className={styles.search}>
          <FormField
            label={t('apiary.locationForm.search')}
            error={geocode.error && !geocodeUnavailable ? errorMessage(geocode.error) : undefined}
          >
            {(p) => (
              <Input
                {...p}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    search();
                  }
                }}
                endAdornment={
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={geocode.isPending}
                    onClick={search}
                    aria-label={t('apiary.locationForm.searchButton')}
                    iconStart={<Icon name="search" size="sm" />}
                  />
                }
              />
            )}
          </FormField>
          {results && (
            <div className={styles.results}>
              <List label={t('apiary.locationForm.searchResults')}>
                {results.length === 0 && (
                  <ListRow
                    title={
                      <Text size="sm" tone="muted">
                        {t('apiary.locationForm.searchEmpty')}
                      </Text>
                    }
                  />
                )}
                {results.map((r) => (
                  <ListRow
                    key={`${r.latitude},${r.longitude}`}
                    title={r.label}
                    onClick={() => {
                      pick(r);
                      if (!address) setAddress(r.label);
                      setResults(null);
                    }}
                  />
                ))}
              </List>
            </div>
          )}
        </div>
      )}
      {geocodeUnavailable && (
        <Text size="sm" tone="muted">
          {t('apiary.locationForm.searchUnavailable')}
        </Text>
      )}
      <div className={styles.mapHeader}>
        <Text as="span" size="sm" weight="medium">
          {t('apiary.locationForm.position')}
        </Text>
        <Button variant="ghost" size="sm" loading={locating} onClick={locate}>
          {t('apiary.locationForm.currentPosition')}
        </Button>
      </div>
      {meta.data && (
        <MapView
          tileUrl={meta.data.map_tile_url}
          attribution={meta.data.map_attribution}
          pick={position}
          onPick={pick}
          height="18rem"
          label={t('apiary.locationForm.position')}
          labels={{
            zoomIn: t('apiary.map.zoomIn'),
            zoomOut: t('apiary.map.zoomOut'),
            unavailable: t('apiary.map.unavailable'),
            tilesUnavailable: t('apiary.map.tilesUnavailable'),
          }}
        />
      )}
      <Text
        size="xs"
        tone={missingPosition || errors.field.position ? 'danger' : 'muted'}
        role={missingPosition ? 'alert' : undefined}
      >
        {missingPosition
          ? t('apiary.locationForm.positionMissing')
          : (errors.field.position ?? t('apiary.locationForm.positionHint'))}
        {locateError && ` ${t('apiary.locationForm.locateFailed')}`}
      </Text>
      <div className={styles.row}>
        <FormField label={t('apiary.locationForm.latitude')}>
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={position?.latitude ?? ''}
              onChange={(e) => {
                const v = Number(e.target.value.replace(',', '.'));
                if (!Number.isNaN(v)) pick({ latitude: v, longitude: position?.longitude ?? 10 });
              }}
            />
          )}
        </FormField>
        <FormField label={t('apiary.locationForm.longitude')}>
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={position?.longitude ?? ''}
              onChange={(e) => {
                const v = Number(e.target.value.replace(',', '.'));
                if (!Number.isNaN(v)) pick({ latitude: position?.latitude ?? 51, longitude: v });
              }}
            />
          )}
        </FormField>
      </div>
      <FormField label={t('apiary.locationForm.address')}>
        {(p) => (
          <Input
            {...p}
            value={address}
            maxLength={200}
            onChange={(e) => setAddress(e.target.value)}
          />
        )}
      </FormField>
      <FormField label={t('apiary.locationForm.access')}>
        {(p) => (
          <Textarea {...p} rows={3} value={access} onChange={(e) => setAccess(e.target.value)} />
        )}
      </FormField>
      <FormField label={t('apiary.locationForm.ownerContact')}>
        {(p) => (
          <Input
            {...p}
            value={ownerContact}
            maxLength={200}
            onChange={(e) => setOwnerContact(e.target.value)}
          />
        )}
      </FormField>
      <FormField label={t('apiary.locationForm.notes')}>
        {(p) => (
          <Textarea {...p} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}
